import { useEffect, useState } from "react";
import axios from "axios";

import { pesosFromCentavos } from "../../lib/driverService";

// Task 5: "How would you like to drive?" on the public booking form. The
// customer chooses a service, never a driver. "Hire a driver" appears only
// when the server says this business may offer it for this vehicle and dates;
// no driver names, counts or internal reasons are ever shown.
export default function DrivingModeSelector({ api, companyId, vehicle, start, end, value, onChange }) {
  const [option, setOption] = useState(null);
  const offersDriver = !!vehicle?.driving_options?.with_driver;
  const selfDrive = vehicle?.driving_options ? vehicle.driving_options.self_drive !== false : true;

  useEffect(() => {
    if (!offersDriver || !start || !end) { setOption(null); return; }
    let cancelled = false;
    axios.post(`${api}/public/business/${companyId}/driver-option`,
      { item_id: vehicle.id, start_datetime: start, end_datetime: end })
      .then((r) => { if (!cancelled) setOption(r.data); })
      .catch(() => { if (!cancelled) setOption({ with_driver: false }); });
    return () => { cancelled = true; };
  }, [api, companyId, vehicle?.id, offersDriver, start, end]);

  const driverAvailable = !!option?.with_driver;
  useEffect(() => {
    if (!selfDrive && driverAvailable && value.mode !== "with_driver") onChange({ ...value, mode: "with_driver" });
    if (value.mode === "with_driver" && option && !driverAvailable) onChange({ ...value, mode: "self_drive" });
  }, [selfDrive, driverAvailable, option, value, onChange]);

  if (!offersDriver) return null;
  const seats = option?.passenger_seats;

  return (
    <fieldset className="rounded-lg border border-slate-200 p-3 space-y-2" data-testid="public-driving-mode">
      <legend className="px-1 text-sm font-semibold">How would you like to drive?</legend>
      <div className="grid sm:grid-cols-2 gap-2">
        {selfDrive && (
          <label className={`flex items-start gap-2 rounded-lg border p-2 cursor-pointer ${value.mode === "self_drive" ? "border-emerald-500 bg-emerald-50" : "border-slate-200"}`}>
            <input type="radio" name="driving-mode" checked={value.mode === "self_drive"} data-testid="public-mode-self"
                   onChange={() => onChange({ ...value, mode: "self_drive" })} className="mt-1" />
            <span><span className="font-medium text-sm">Self-drive</span>
              <span className="block text-xs text-slate-500">You drive the vehicle.</span></span>
          </label>
        )}
        <label className={`flex items-start gap-2 rounded-lg border p-2 ${driverAvailable ? "cursor-pointer" : "opacity-60"} ${value.mode === "with_driver" ? "border-emerald-500 bg-emerald-50" : "border-slate-200"}`}>
          <input type="radio" name="driving-mode" disabled={!driverAvailable} checked={value.mode === "with_driver"}
                 data-testid="public-mode-driver" onChange={() => onChange({ ...value, mode: "with_driver" })} className="mt-1" />
          <span><span className="font-medium text-sm">Hire a driver</span>
            <span className="block text-xs text-slate-500">
              {!start || !end ? "Choose your dates to check."
                : !option ? "Checking…"
                  : !driverAvailable ? (option.with_driver_message || "Not available for these dates.")
                    : option.with_driver_message || "The rental business assigns a qualified driver."}
            </span></span>
        </label>
      </div>

      {value.mode === "with_driver" && driverAvailable && (
        <div className="grid sm:grid-cols-2 gap-2 text-sm">
          <label><span className="text-xs text-slate-500">Passengers{seats ? ` (up to ${seats})` : ""}</span>
            <input type="number" min="1" max={seats || undefined} required value={value.passenger_count}
                   data-testid="public-passenger-count"
                   onChange={(e) => onChange({ ...value, passenger_count: e.target.value })}
                   className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300" /></label>
          <label><span className="text-xs text-slate-500">Destination (optional)</span>
            <input value={value.destination} onChange={(e) => onChange({ ...value, destination: e.target.value })}
                   className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300" /></label>
          <div className="sm:col-span-2 text-xs text-slate-600" data-testid="public-driver-price">
            Driver service: {option.quote?.quote_required ? "price confirmed by the business after your request"
              : option.quote?.amount_centavos === 0 ? "included in the vehicle rate"
                : pesosFromCentavos(option.quote?.amount_centavos)}
          </div>
          {option.terms && Object.keys(option.terms).length > 1 && (
            <div className="sm:col-span-2 rounded bg-slate-50 border border-slate-200 p-2 text-xs text-slate-600">
              <div className="font-semibold">{option.terms.label}</div>
              {Object.entries(option.terms).filter(([k]) => k !== "label").map(([k, v]) => <div key={k}>{v}</div>)}
            </div>
          )}
        </div>
      )}
    </fieldset>
  );
}
